import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  Stack,
} from "@mui/material";
import {
  CalendarToday,
  Person,
  AttachMoney,
  CheckCircle,
  Info,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";

export interface AnimalDetail {
  tag: string;
  name: string;
  _id: string;
  breed_code: string;
  gender: string;
  current_status: string;
}

export interface CattleId {
  tag: string;
  country_code: string;
  manufacturer_code: number;
  unique_id: string;
}

export interface EntityProfile {
  entity_type: string;
  _id: string;
  _key: string;
  _rev: string;
  farm_id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  // Campos específicos para lotes
  cattle_tags?: string[];
  total_animals?: number;
  // Campos específicos para animais
  cattle_id?: CattleId;
  breed_code?: string;
  gender?: string;
  birth_date?: string | null;
  birth_weight_kg?: number | null;
  birth_farm_id?: string | null;
  current_farm_id?: string;
  current_paddock_id?: string | null;
  production_purpose?: string | null;
  current_status?: string;
  death_date?: string | null;
  death_reason?: string | null;
}

export interface HealthData {
  api_message: string;
  entity_type: string; // "lote" ou "animal"
  handling_type_code: string;
  handling_date: string;
  performed_by: string;
  notes: string;
  cost: number;
  handling_details: {
    // Campos comuns para diferentes tipos de atendimento
    dosage_ml?: string | number;
    // Vacinação / parasitas
    disease?: string;
    vaccine_name?: string;
    route?: string;
    // Cuidados gerais
    description_of_service?: string;
    product_name?: string;
  };
  operation_status: string;
  records_created: number;
  // Novos campos para lotes
  entity_profile?: EntityProfile;
  search_performed?: boolean;
  search_reason?: string;
  lote_name?: string;
  operation_lot_id?: string;
  animals_processed?: number;
  cattle_with_details?: AnimalDetail[];
  animals_found?: number;
  animals_not_found?: number;
  // Para animais individuais
  cattle_tag?: string;
}

export interface HealthDetailsData {
  data: HealthData;
  resumo?: string;
  url?: string;
}

export interface HealthDetailsViewProps {
  healthDetailsData: HealthDetailsData;
}

const formatDate = (dateString?: string, t?: any): string => {
  if (!dateString) return t ? t('common:labels.date_not_available') : "Data não disponível";
  try {
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      const locale = t ? t('common:locale') : 'pt-BR';
      const d = new Date(`${year}-${month}-${day}T00:00:00`);
      return d.toLocaleDateString(locale);
    }
    const locale = t ? t('common:locale') : 'pt-BR';
    return new Date(dateString).toLocaleDateString(locale);
  } catch {
    return dateString;
  }
};

const getStatusColor = (status: string) => {
  switch (status.toUpperCase()) {
    case 'COMPLETO':
      return DESIGN_TOKENS.colors.brand.primary;
    case 'PENDENTE':
      return DESIGN_TOKENS.colors.status.warning[500];
    case 'ERRO':
      return DESIGN_TOKENS.colors.status.error[500];
    default:
      return DESIGN_TOKENS.colors.status.info[500];
  }
};

const getStatusLabel = (status: string, t: any) => {
  switch (status.toUpperCase()) {
    case 'COMPLETO':
      return t('common:status.completed');
    case 'PENDENTE':
      return t('common:status.pending');
    case 'ERRO':
      return t('common:status.error');
    default:
      return status;
  }
};

const getProcedureTypeLabel = (code: string, t: any) => {
  switch (code) {
    case 'registrar_vacinacao':
      return t('farm:healthDetails.recordVaccination');
    case 'registrar_cuidados':
      return t('farm:healthDetails.recordCare');
    case 'registrar_medicacao':
      return t('farm:healthDetails.recordMedication');
    case 'registrar_ectoparasita':
      return t('farm:healthDetails.recordEctoparasite');
    case 'registrar_endoparasita':
      return t('farm:healthDetails.recordEndoparasite');
    case 'fornecimento_nutrientes':
      return t('farm:healthDetails.supplyNutrients');
    default:
      return code.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
};

const formatCurrency = (amount: number, t?: any): string => {
  const symbol = t ? t('common:currency.symbol') : 'R$';
  const currency = symbol === 'R$' ? 'BRL' : symbol === '$' ? 'USD' : 'USD';
  const locale = t ? t('common:locale') : 'pt-BR';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
};

// Extrai a tag do animal a partir de diferentes fontes do payload
const getTreatedAnimalTag = (healthDetailsData: HealthDetailsData): string | null => {
  const { data: healthData, resumo } = healthDetailsData;

  // 1) Se já vier no payload
  if (healthData?.cattle_tag) return String(healthData.cattle_tag);

  // 2) Tenta extrair do resumo (ex.: Atendimento Sanitário registrado para o animal "001")
  if (typeof resumo === 'string') {
    const m = resumo.match(/animal\s+"?([A-Za-z0-9._-]+)"?/i);
    if (m && m[1]) return m[1];
  }

  // 3) Tenta extrair da mensagem da API (ex.: ... registrado para o animal 001.)
  if (typeof healthData?.api_message === 'string') {
    const m = healthData.api_message.match(/animal\s+"?([A-Za-z0-9._-]+)"?/i);
    if (m && m[1]) return m[1];
  }

  // 4) Fallback opcional: nome do perfil, se existir
  if (healthData?.entity_profile?.name) return healthData.entity_profile.name;

  return null;
};

export const HealthDetailsView: React.FC<HealthDetailsViewProps> = ({ healthDetailsData }) => {
  const { t } = useTranslation(['farm','common']);
  const { data: healthData } = healthDetailsData;
  const treatedAnimalTag = getTreatedAnimalTag(healthDetailsData);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>


      {/* Status Card */}
      <Card sx={{ mb: 3, bgcolor: DESIGN_TOKENS.colors.background.elevated }}>
        <CardContent>
          <Stack direction="row" spacing={4} alignItems="center">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckCircle sx={{ color: getStatusColor(healthData.operation_status) }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('farm:healthDetails.operationStatus')}
                </Typography>
                <Chip
                  label={getStatusLabel(healthData.operation_status, t)}
                  size="small"
                  sx={{
                    bgcolor: getStatusColor(healthData.operation_status),
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
            {healthData.entity_type === 'lote' ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img 
                  src="/vaca.png" 
                  alt={t('common:alt_texts.cow')} 
                  style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('farm:healthDetails.animalsProcessed')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {healthData.animals_processed || 0}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img 
                  src="/vaca.png" 
                  alt={t('common:alt_texts.cow')} 
                  style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('farm:healthDetails.treatedAnimal')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {treatedAnimalTag || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Info sx={{ color: DESIGN_TOKENS.colors.organization.farm }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('farm:healthDetails.recordsCreated')}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {healthData.records_created}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Main Details */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Informações Gerais */}
        <Box sx={{ width: { xs: '100%', md: 'calc(50% - 12px)' } }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: DESIGN_TOKENS.colors.organization.farm,
                    width: 40,
                    height: 40,
                  }}
                >
                  <Box
                    component="img"
                    src="/gestores/seringa.svg"
                    alt={t('common:alt_texts.syringe')}
                    sx={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
                  />
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: DESIGN_TOKENS.colors.text.primary,
                  }}
                >
                  {t('farm:healthDetails.generalInformation')}
                </Typography>
              </Box>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {healthData.entity_type === 'animal' ? t('common:entities.animal') : t('common:entities.lot')}
                  </Typography>
                  <Typography variant="body1">
                    {healthData.entity_type === 'animal' ? (treatedAnimalTag || 'N/A') : healthData.lote_name}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('farm:healthDetails.procedureType')}
                  </Typography>
                  <Typography variant="body1">
                    {getProcedureTypeLabel(healthData.handling_type_code, t)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarToday sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('farm:healthDetails.procedureDate')}
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(healthData.handling_date, t)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Person sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('farm:healthDetails.performedBy')}
                    </Typography>
                    <Typography variant="body1">
                      {healthData.performed_by}
                    </Typography>
                  </Box>
                </Box>

                {healthData.cost > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AttachMoney sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {t('farm:healthDetails.cost')}
                      </Typography>
                      <Typography variant="body1">
                        {formatCurrency(healthData.cost, t)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Detalhes da Vacinação */}
        <Box sx={{ width: { xs: '100%', md: 'calc(50% - 12px)' } }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: '#424242',
                    width: 40,
                    height: 40,
                  }}
                >
                  <Box
                    component="img"
                    src="/gestores/seringa.svg"
                    alt={t('common:alt_texts.syringe')}
                    sx={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
                  />
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: DESIGN_TOKENS.colors.text.primary,
                  }}
                >
                  {t('farm:healthDetails.treatmentDetails')}
                </Typography>
              </Box>

              <Stack spacing={2}>
                {/* Doença (vacinação/parasitário) */}
                {healthData?.handling_details?.disease && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('farm:healthDetails.disease')}
                    </Typography>
                    <Typography variant="body1">
                      {healthData.handling_details.disease}
                    </Typography>
                  </Box>
                )}

                {/* Nome da Vacina (vacinação) */}
                {healthData?.handling_details?.vaccine_name && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('farm:healthDetails.vaccineName')}
                    </Typography>
                    <Typography variant="body1">
                      {healthData.handling_details.vaccine_name}
                    </Typography>
                  </Box>
                )}

                {/* Descrição do Serviço (cuidados gerais) */}
                {healthData?.handling_details?.description_of_service && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('farm:healthDetails.serviceDescription')}
                    </Typography>
                    <Typography variant="body1">
                      {healthData.handling_details.description_of_service}
                    </Typography>
                  </Box>
                )}

                {/* Produto (cuidados gerais) */}
                {healthData?.handling_details?.product_name && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('farm:healthDetails.product')}
                    </Typography>
                    <Typography variant="body1">
                      {healthData.handling_details.product_name}
                    </Typography>
                  </Box>
                )}

                {/* Dosagem (ml) - comum */}
                {typeof healthData?.handling_details?.dosage_ml !== 'undefined' && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('farm:healthDetails.dosageMl')}
                    </Typography>
                    <Typography variant="body1">
                      {typeof healthData.handling_details.dosage_ml === 'number'
                        ? healthData.handling_details.dosage_ml
                        : healthData.handling_details.dosage_ml || '[não especificado]'}
                    </Typography>
                  </Box>
                )}

                {/* Via de Administração (vacinação/parasitário) */}
                {healthData?.handling_details?.route && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('farm:healthDetails.administrationRoute')}
                    </Typography>
                    <Typography variant="body1">
                      {healthData.handling_details.route}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Informações da Entidade */}
      {healthData.entity_profile && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: DESIGN_TOKENS.colors.text.primary,
                mb: 2,
              }}
            >
              {healthData.entity_profile.entity_type === 'animal' ? t('farm:healthDetails.animalInformation') : t('farm:healthDetails.lotInformation')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 300px' }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {healthData.entity_profile.entity_type === 'animal' ? t('farm:healthDetails.animalName') : t('farm:healthDetails.lotName')}
                    </Typography>
                    <Typography variant="body1">
                      {healthData.entity_profile.name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {healthData.entity_profile.entity_type === 'animal' ? t('farm:healthDetails.animalId') : t('farm:healthDetails.lotId')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {healthData.entity_profile._key}
                    </Typography>
                  </Box>
                  {healthData.entity_profile.entity_type === 'animal' ? (
                    <>
                      {healthData.entity_profile.cattle_id && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('farm:healthDetails.rfidTag')}
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                            {healthData.entity_profile.cattle_id.tag}
                          </Typography>
                        </Box>
                      )}
                      {healthData.entity_profile.breed_code && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('farm:healthDetails.breed')}
                          </Typography>
                          <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                            {healthData.entity_profile.breed_code}
                          </Typography>
                        </Box>
                      )}
                      {healthData.entity_profile.gender && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('farm:healthDetails.gender')}
                          </Typography>
                          <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                            {healthData.entity_profile.gender}
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {t('farm:healthDetails.totalAnimalsInLot')}
                      </Typography>
                      <Typography variant="body1">
                        {healthData.entity_profile.total_animals}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Stack spacing={2}>
                  {healthData.entity_profile.entity_type === 'animal' && (
                    <>
                      {healthData.entity_profile.current_status && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('farm:healthDetails.currentStatus')}
                          </Typography>
                          <Chip
                            label={healthData.entity_profile.current_status}
                            size="small"
                            sx={{
                              bgcolor: healthData.entity_profile.current_status === 'active' ? '#4CAF50' : '#FFC107',
                              color: 'white',
                              textTransform: 'capitalize'
                            }}
                          />
                        </Box>
                      )}
                      {healthData.entity_profile.birth_date && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('farm:healthDetails.birthDate')}
                          </Typography>
                          <Typography variant="body1">
                            {formatDate(healthData.entity_profile.birth_date, t)}
                          </Typography>
                        </Box>
                      )}
                      {healthData.entity_profile.production_purpose && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('farm:healthDetails.productionPurpose')}
                          </Typography>
                          <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                            {healthData.entity_profile.production_purpose}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('common:created_at')}
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(healthData.entity_profile.created_at, t)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('common:last_updated')}
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(healthData.entity_profile.updated_at, t)}
                    </Typography>
                  </Box>
                  {healthData.search_performed && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {t('farm:healthDetails.searchStatus')}
                      </Typography>
                      <Chip
                        label={healthData.search_reason || t('farm:healthDetails.searchPerformed')}
                        size="small"
                        color="success"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  )}
                </Stack>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Lista de Animais Processados */}
      {healthData.cattle_with_details && healthData.cattle_with_details.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: DESIGN_TOKENS.colors.text.primary,
                mb: 2,
              }}
            >
              {t('farm:healthDetails.animalsProcessed')} ({healthData.cattle_with_details.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {healthData.cattle_with_details.map((animal, index) => (
                <Card key={index} variant="outlined" sx={{ minWidth: 200, flex: '1 1 calc(33.333% - 16px)' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <img 
                        src="/vaca.png" 
                        alt={t('common:alt_texts.animal')} 
                        style={{ width: 16, height: 16, filter: 'brightness(0)' }}
                      />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {animal.name}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('farm:healthDetails.tag')}: {animal.tag}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('farm:healthDetails.breed')}: {animal.breed_code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('farm:healthDetails.gender')}: {animal.gender}
                    </Typography>
                    <Chip
                      label={animal.current_status}
                      size="small"
                      sx={{
                        bgcolor: animal.current_status === 'active' ? '#4CAF50' : '#FFC107',
                        color: 'white',
                        fontSize: '0.7rem',
                        mt: 1,
                      }}
                    />
                  </CardContent>
                </Card>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Estatísticas de Processamento */}
      {(healthData.animals_found !== undefined || healthData.animals_not_found !== undefined) && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: DESIGN_TOKENS.colors.text.primary,
                mb: 2,
              }}
            >
              {t('farm:healthDetails.processingStatistics')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {healthData.animals_found !== undefined && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: DESIGN_TOKENS.colors.status.success[500],
                    }}
                  >
                    {healthData.animals_found}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
                  >
                    {t('farm:healthDetails.animalsFound')}
                  </Typography>
                </Box>
              )}
              {healthData.animals_not_found !== undefined && healthData.animals_not_found > 0 && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: DESIGN_TOKENS.colors.status.warning[500],
                    }}
                  >
                    {healthData.animals_not_found}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
                  >
                    {t('farm:healthDetails.animalsNotFound')}
                  </Typography>
                </Box>
              )}
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: DESIGN_TOKENS.colors.organization.farm,
                  }}
                >
                  {healthData.records_created}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
                >
                  {t('farm:healthDetails.recordsCreated')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Observações */}
      {healthData.notes && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: DESIGN_TOKENS.colors.text.primary,
                mb: 2,
              }}
            >
              {t('farm:healthDetails.observations')}
            </Typography>
            <Typography variant="body1">
              {healthData.notes}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default HealthDetailsView;